import { useEffect, useState } from "react";
import styled from "styled-components";
import Select from "react-select";
import {
  AGE_GROUP_OPTIONS,
  GENDER_OPTIONS,
  BREED_OPTIONS,
  COLOR_OPTIONS,
  COAT_PATTERN_OPTIONS,
  BODY_SIZE_OPTIONS,
  BODY_CONDITION_OPTIONS,
  REPRODUCTIVE_OPTIONS,
  IDENTIFICATION_OPTIONS,
  REGION_SELECT_OPTIONS,
  INJURY_SELECT_OPTIONS,
  multiEnumSelectStyles,
  dogToEditForm,
} from "../utils/dogEditForm";

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalPanel = styled.div`
  background: #fffdf9;
  border-radius: 16px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  border: 1px solid #ffedd5;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 2px solid #ffedd5;
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 700;
`;

const ModalClose = styled.button`
  background: transparent;
  border: none;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;
  padding: 4px 8px;
  border-radius: 8px;
  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }
`;

const ModalBody = styled.div`
  overflow-y: auto;
  padding: 16px 20px;
  flex: 1;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #f3f4f6;
  flex-shrink: 0;
`;

const FormSection = styled.div`
  margin-bottom: 20px;
`;

const FormSectionTitle = styled.h4`
  margin: 0 0 10px 0;
  color: #374151;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 4px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #f97316;
    box-shadow: 0 0 0 2px rgba(249, 115, 22, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
  font-family: inherit;
  box-sizing: border-box;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #f97316;
    box-shadow: 0 0 0 2px rgba(249, 115, 22, 0.2);
  }
`;

const EnumSelect = styled.select`
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
  box-sizing: border-box;
  background: #fff;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: #f97316;
    box-shadow: 0 0 0 2px rgba(249, 115, 22, 0.2);
  }
`;

const MultiEnumSelectWrap = styled.div`
  width: 100%;
  font-size: 14px;
`;

const ModalError = styled.div`
  background: #fef2f2;
  color: #991b1b;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 12px;
`;

const CancelModalBtn = styled.button`
  background: #f3f4f6;
  color: #374151;
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    background: #e5e7eb;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SaveModalBtn = styled.button`
  background: #059669;
  color: #fff;
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    background: #047857;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default function DogEditModal({
  dog,
  initialForm = null,
  title,
  saving = false,
  error = null,
  submitLabel = "Save changes",
  savingLabel = "Saving…",
  onClose,
  onSave,
}) {
  const [editForm, setEditForm] = useState(() => {
    if (initialForm) return JSON.parse(JSON.stringify(initialForm));
    if (dog) return dogToEditForm(dog);
    return null;
  });
  const formSourceId = initialForm ? "__filter__" : dog?._id ?? null;

  useEffect(() => {
    if (!formSourceId) {
      setEditForm(null);
      return;
    }
    if (initialForm) {
      setEditForm(JSON.parse(JSON.stringify(initialForm)));
      return;
    }
    if (dog) {
      setEditForm(dogToEditForm(dog));
    }
    // Snapshot the form when the modal opens. A new initialForm object on
    // each parent render must not wipe in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSourceId]);

  if ((!dog && !initialForm) || !editForm) return null;

  const updateDemo = (key, value) => {
    setEditForm((prev) =>
      prev
        ? {
            ...prev,
            demographics: { ...prev.demographics, [key]: value },
          }
        : prev,
    );
  };

  const updateAppearance = (key, value) => {
    setEditForm((prev) =>
      prev
        ? {
            ...prev,
            appearance: { ...prev.appearance, [key]: value },
          }
        : prev,
    );
  };

  const updateBody = (key, value) => {
    setEditForm((prev) =>
      prev
        ? {
            ...prev,
            body: { ...prev.body, [key]: value },
          }
        : prev,
    );
  };

  const handleSave = () => {
    if (editForm) onSave(editForm);
  };

  return (
    <ModalOverlay
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-dog-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <ModalPanel onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle id="edit-dog-modal-title">{title}</ModalTitle>
          <ModalClose
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={saving}
          >
            ×
          </ModalClose>
        </ModalHeader>
        <ModalBody>
          {error && <ModalError>{error}</ModalError>}
          <FormSection>
            <FormSectionTitle>Demographics</FormSectionTitle>
            <FormGrid>
              <div>
                <FieldLabel htmlFor="demo-ageGroup">Age group</FieldLabel>
                <EnumSelect
                  id="demo-ageGroup"
                  value={editForm.demographics.ageGroup ?? ""}
                  onChange={(e) => updateDemo("ageGroup", e.target.value)}
                >
                  <option value="">—</option>
                  {AGE_GROUP_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </EnumSelect>
              </div>
              <div>
                <FieldLabel htmlFor="demo-gender">Gender</FieldLabel>
                <EnumSelect
                  id="demo-gender"
                  value={editForm.demographics.gender ?? ""}
                  onChange={(e) => updateDemo("gender", e.target.value)}
                >
                  <option value="">—</option>
                  {GENDER_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </EnumSelect>
              </div>
              <div>
                <FieldLabel htmlFor="demo-breed">Breed</FieldLabel>
                <EnumSelect
                  id="demo-breed"
                  value={editForm.demographics.breed ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEditForm((prev) =>
                      prev
                        ? {
                            ...prev,
                            demographics: {
                              ...prev.demographics,
                              breed: v,
                              ...(v !== "OTHER" ? { breedOther: "" } : {}),
                            },
                          }
                        : prev,
                    );
                  }}
                >
                  <option value="">—</option>
                  {BREED_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </EnumSelect>
              </div>
              {editForm.demographics.breed === "OTHER" && (
                <div>
                  <FieldLabel htmlFor="demo-breedOther">Breed (other)</FieldLabel>
                  <TextInput
                    id="demo-breedOther"
                    value={editForm.demographics.breedOther ?? ""}
                    onChange={(e) => updateDemo("breedOther", e.target.value)}
                  />
                </div>
              )}
            </FormGrid>
          </FormSection>
          <FormSection>
            <FormSectionTitle>Appearance</FormSectionTitle>
            <FormGrid>
              <div>
                <FieldLabel htmlFor="app-primary">Primary color</FieldLabel>
                <EnumSelect
                  id="app-primary"
                  value={editForm.appearance.primaryColor ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEditForm((prev) =>
                      prev
                        ? {
                            ...prev,
                            appearance: {
                              ...prev.appearance,
                              primaryColor: v,
                              ...(v !== "OTHER" ? { primaryColorOther: "" } : {}),
                            },
                          }
                        : prev,
                    );
                  }}
                >
                  <option value="">—</option>
                  {COLOR_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </EnumSelect>
              </div>
              {editForm.appearance.primaryColor === "OTHER" && (
                <div>
                  <FieldLabel htmlFor="app-primaryOther">
                    Primary color (other)
                  </FieldLabel>
                  <TextInput
                    id="app-primaryOther"
                    value={editForm.appearance.primaryColorOther ?? ""}
                    onChange={(e) =>
                      updateAppearance("primaryColorOther", e.target.value)
                    }
                  />
                </div>
              )}
              <div>
                <FieldLabel htmlFor="app-secondary">Secondary color</FieldLabel>
                <EnumSelect
                  id="app-secondary"
                  value={editForm.appearance.secondaryColor ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEditForm((prev) =>
                      prev
                        ? {
                            ...prev,
                            appearance: {
                              ...prev.appearance,
                              secondaryColor: v,
                              ...(v !== "OTHER" ? { secondaryColorOther: "" } : {}),
                            },
                          }
                        : prev,
                    );
                  }}
                >
                  <option value="">—</option>
                  {COLOR_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </EnumSelect>
              </div>
              {editForm.appearance.secondaryColor === "OTHER" && (
                <div>
                  <FieldLabel htmlFor="app-secondaryOther">
                    Secondary color (other)
                  </FieldLabel>
                  <TextInput
                    id="app-secondaryOther"
                    value={editForm.appearance.secondaryColorOther ?? ""}
                    onChange={(e) =>
                      updateAppearance("secondaryColorOther", e.target.value)
                    }
                  />
                </div>
              )}
              <div>
                <FieldLabel htmlFor="app-coat">Coat pattern</FieldLabel>
                <EnumSelect
                  id="app-coat"
                  value={editForm.appearance.coatPattern ?? ""}
                  onChange={(e) => updateAppearance("coatPattern", e.target.value)}
                >
                  <option value="">—</option>
                  {COAT_PATTERN_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o.replace(/_/g, " ")}
                    </option>
                  ))}
                </EnumSelect>
              </div>
              <div>
                <FieldLabel htmlFor="app-mixed">Mixed (coat)</FieldLabel>
                <TextInput
                  id="app-mixed"
                  value={editForm.appearance.mixed ?? ""}
                  onChange={(e) => updateAppearance("mixed", e.target.value)}
                />
              </div>
            </FormGrid>
          </FormSection>
          <FormSection>
            <FormSectionTitle>Body</FormSectionTitle>
            <FormGrid>
              <div>
                <FieldLabel htmlFor="body-size">Size</FieldLabel>
                <EnumSelect
                  id="body-size"
                  value={editForm.body.size ?? ""}
                  onChange={(e) => updateBody("size", e.target.value)}
                >
                  <option value="">—</option>
                  {BODY_SIZE_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </EnumSelect>
              </div>
              <div>
                <FieldLabel htmlFor="body-condition">Body condition</FieldLabel>
                <EnumSelect
                  id="body-condition"
                  value={editForm.body.bodyCondition ?? ""}
                  onChange={(e) => updateBody("bodyCondition", e.target.value)}
                >
                  <option value="">—</option>
                  {BODY_CONDITION_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </EnumSelect>
              </div>
            </FormGrid>
          </FormSection>
          <FormSection>
            <FormSectionTitle>Reproductive status</FormSectionTitle>
            <EnumSelect
              id="repro-status"
              value={editForm.reproductiveStatus ?? ""}
              onChange={(e) =>
                setEditForm((prev) =>
                  prev ? { ...prev, reproductiveStatus: e.target.value } : prev,
                )
              }
            >
              <option value="">—</option>
              {REPRODUCTIVE_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o.replace(/_/g, " ")}
                </option>
              ))}
            </EnumSelect>
          </FormSection>
          <FormSection>
            <FormSectionTitle>Identification</FormSectionTitle>
            <FieldLabel htmlFor="id-type">Type</FieldLabel>
            <EnumSelect
              id="id-type"
              value={editForm.identification ?? ""}
              onChange={(e) =>
                setEditForm((prev) =>
                  prev ? { ...prev, identification: e.target.value } : prev,
                )
              }
            >
              <option value="">—</option>
              {IDENTIFICATION_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </EnumSelect>
          </FormSection>
          <FormSection>
            <FormSectionTitle>Scars</FormSectionTitle>
            <FieldLabel htmlFor="scars-multi">Body regions</FieldLabel>
            <MultiEnumSelectWrap>
              <Select
                inputId="scars-multi"
                classNamePrefix="dog-edit"
                isMulti
                options={REGION_SELECT_OPTIONS}
                value={editForm.scars.map((v) => ({
                  value: v,
                  label: v,
                }))}
                onChange={(opts) =>
                  setEditForm((prev) =>
                    prev
                      ? { ...prev, scars: opts ? opts.map((o) => o.value) : [] }
                      : prev,
                  )
                }
                placeholder="Select one or more"
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                styles={multiEnumSelectStyles}
              />
            </MultiEnumSelectWrap>
          </FormSection>
          <FormSection>
            <FormSectionTitle>Spots</FormSectionTitle>
            <FieldLabel htmlFor="spots-multi">Body regions</FieldLabel>
            <MultiEnumSelectWrap>
              <Select
                inputId="spots-multi"
                classNamePrefix="dog-edit"
                isMulti
                options={REGION_SELECT_OPTIONS}
                value={editForm.spots.map((v) => ({
                  value: v,
                  label: v,
                }))}
                onChange={(opts) =>
                  setEditForm((prev) =>
                    prev
                      ? { ...prev, spots: opts ? opts.map((o) => o.value) : [] }
                      : prev,
                  )
                }
                placeholder="Select one or more"
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                styles={multiEnumSelectStyles}
              />
            </MultiEnumSelectWrap>
          </FormSection>
          <FormSection>
            <FormSectionTitle>Injuries</FormSectionTitle>
            <FieldLabel htmlFor="injuries-multi">Regions / type</FieldLabel>
            <MultiEnumSelectWrap>
              <Select
                inputId="injuries-multi"
                classNamePrefix="dog-edit"
                isMulti
                options={INJURY_SELECT_OPTIONS}
                value={editForm.injuries.map((v) => ({
                  value: v,
                  label: v.replace(/_/g, " "),
                }))}
                onChange={(opts) =>
                  setEditForm((prev) =>
                    prev
                      ? {
                          ...prev,
                          injuries: opts ? opts.map((o) => o.value) : [],
                        }
                      : prev,
                  )
                }
                placeholder="Select one or more"
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                styles={multiEnumSelectStyles}
              />
            </MultiEnumSelectWrap>
          </FormSection>
          <FormSection>
            <FormSectionTitle>Remarks</FormSectionTitle>
            <FieldLabel htmlFor="remarks">Notes</FieldLabel>
            <TextArea
              id="remarks"
              value={editForm.remarks ?? ""}
              onChange={(e) =>
                setEditForm((prev) =>
                  prev ? { ...prev, remarks: e.target.value } : prev,
                )
              }
              placeholder="Add remarks…"
            />
          </FormSection>
        </ModalBody>
        <ModalFooter>
          <CancelModalBtn type="button" onClick={onClose} disabled={saving}>
            Cancel
          </CancelModalBtn>
          <SaveModalBtn type="button" onClick={handleSave} disabled={saving}>
            {saving ? savingLabel : submitLabel}
          </SaveModalBtn>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );
}
